import { useCallback, useEffect, useRef, useState } from "react";

/**
 * 「按可用**框**取景」——把一张已知比例的图/视频塞进一个已知宽高的盒子里(contain)。
 *
 * ★2026-08-07 从 `features/poster/ui/components/canvas/` 上移到 shared:分身舱结果页
 *   踩的是同一个坑(`<video className="max-h-[60vh] w-full">`,竖版口播在 1440 上画面
 *   只占 43.5%、1920 上 34.4%,黑边 527/926px),取景范式两舱同源,不再各写一份。
 *
 * ═══ 为什么要这个 hook(2026-08-07 实测)═══════════════════════════════════════
 * 视觉舱所有产物此前都是**宽度驱动**取景:`width:100%` + `aspect-ratio`(PosterCanvas)
 * 或 `w-full` + `max-h-[70vh]`(长图整图)。宽度决定高度 = **视口高度从来没参与过**,
 * 于是屏幕越大越难用 —— 测试机真页面注探针实测:
 *   · 9:16 主图 1440×900 渲成 874×1554(首屏见 43%,整页 2.41 屏)
 *     同一张图 1920×1080 渲成 **1354×2407**(首屏见 31%,整页 **2.80 屏**)
 *   · 长图 1500×8984 在 `max-h-[70vh]` 里 1440 见 8.3%,1920 见 **7.2%**
 * 加宽屏幕反而多滚一屏,这不是手感问题,是取景范式选错了。
 *
 * 改成「盒子驱动」:宽 = min(可用宽, 可用高 × 比例)。图永远整张在框里,视口越大图越大,
 * 但**永远不越屏**。
 *
 * ★为什么用 JS 量而不是纯 CSS:`width:100%` + `aspect-ratio` + `max-height` 三条同时给时,
 *   浏览器会让 aspect-ratio 让位于尺寸约束 —— 盒子变成 100%×maxH,比例被拉坏,底图
 *   `object-cover` 就把画面裁掉了。而 `height:100%; width:auto; max-width:100%` 那套在
 *   grid item 里对「谁是确定高度」很敏感(存量已踩过:items-start 的 grid item 里 h-full
 *   没参照,预览列直接长到 3232px)。显式量、显式给 px,是这一层唯一可预测的写法。
 *
 * ★返回 null(还没量到)时调用方应当**先不渲染图或先给容器**,不要拿 0 去除。
 */
export interface FitBox {
  /** 挂在「可用区」容器上 —— 它必须有确定的宽**和**高(如 flex-1 + min-h-0)。 */
  ref: (el: HTMLElement | null) => void;
  /** contain 之后图该占的 CSS 宽度(px)。量到之前是 null。 */
  width: number | null;
  /** 可用区实测尺寸,给需要按高度反算的调用方(如长图「适高」)。 */
  box: { w: number; h: number } | null;
}

export function useFitBox(aspectRatio: number): FitBox {
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);

  // callback ref:容器可能是条件渲染出来的(等待态/终态切换),用 useRef + useEffect
  // 会错过第一次挂载。
  const ref = useCallback((el: HTMLElement | null) => {
    roRef.current?.disconnect();
    roRef.current = null;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (!r) return;
      setBox((prev) =>
        prev && Math.abs(prev.w - r.width) < 1 && Math.abs(prev.h - r.height) < 1
          ? prev
          : { w: r.width, h: r.height }
      );
    });
    ro.observe(el);
    roRef.current = ro;
  }, []);

  useEffect(() => () => roRef.current?.disconnect(), []);

  const width =
    box && box.w > 0 && box.h > 0 && aspectRatio > 0
      ? Math.max(1, Math.floor(Math.min(box.w, box.h * aspectRatio)))
      : null;

  return { ref, width, box };
}
